<template lang="pug">
article.page.page--article.blog-slug
  error-layout(
    v-if="$fetchState.error"
    :error="$fetchState.error")

  template(v-else)
    .page__cover(v-image="cover" :class="{ clp: $fetchState.pending }")
      .container.framed

        template(v-if="$fetchState.pending")
          .page__name.clp.w-2 &nbsp;
          h1.page__title.clp.w-8 &nbsp;

        template(v-else-if="record")
          nuxt-link.page__name(
            :to="{ name: 'blog' }"
            title="Blog"
            data-aos="fade-down") Blog
          h1.page__title.article__title(
            data-aos="zoom-out"
            v-text="title")
          div
            .article__author {{ record.author || 'Pod' }}
            .article__metas
              span.article__updated {{ record.updatedAt | date('MMM D, YYYY') }}
              span •
              span.article__mins {{ content | minsRead }}

    .container.framed
      loading(v-if="$fetchState.pending")
      .article__wrapper(v-else-if="record")
        .article__actions
          el-breadcrumb.article__breadcrumb(separator="/")
            el-breadcrumb-item(
              :to="{ name: 'blog' }"
              title="Blog") Blog
            el-breadcrumb-item(
              :to="{ name: 'blog', query: { category: record.category } }"
              :title="record.category") {{ record.category }}
            el-breadcrumb-item
          .article__share
            client-only
              share-network(network="facebook" v-bind="share")
                i.bx.bxl-facebook
              share-network(network="twitter" v-bind="share")
                i.bx.bxl-twitter
              share-network(network="linkedin" v-bind="share")
                i.bx.bxl-linkedin
              share-network(network="email" v-bind="share")
                i.bx.bx-envelope

        .article__content(v-html="content")

      //- debug(:data="record" static)
</template>

<script>
import ErrorLayout from '~/layouts/error'

export default {
  components: {
    ErrorLayout
  },

  scrollToTop: true,

  data () {
    return {
      record: null
    }
  },

  fetch () {
    return this.$contentful.client
      .getEntries({
        content_type: 'blogPost',
        limit: 1,
        'fields.slug': this.slug
      })
      .then(res => {
        if (!res.items.length) this.$nuxt.error({ statusCode: 404 })

        this.record = res.items
          .map(i => ({
            ...(i?.fields || {}),
            createdAt: i?.sys?.createdAt,
            updatedAt: i?.sys?.updatedAt
          }))
          ?.[0]
      })
      .catch(e => {
        const error = { statusCode: 503 }
        if (process.server) {
          this.$nuxt.context.res.statusCode = error.statusCode
          this.$fetchState.error = error
        } else {
          this.$nuxt.error(error)
        }
      })
  },

  head () {
    return this.$seo.head(
      this.record
        ? {
          title: this.record.title,
          description: this.record.summary,
          properties: {
            'og:image': this.cover,
            'og:image:type': ''
          }
        }
        : {
          title: 'Blog'
        }
    )
  },

  computed: {
    slug () {
      return this.$route.params.slug
    },

    title () {
      if (!this.record) return 'Blog'
      return this.record.title
    },

    cover () {
      const thumbnail = this.record?.thumbnail?.fields?.file?.url
      const cover = this.record?.cover?.fields?.file?.url
      return cover || thumbnail
    },

    content () {
      return this.record
        ? this.$contentful.html(this.record.content)
        : ''
    },

    share () {
      return {
        title: this.title,
        description: this.record?.summary || undefined,
        url: process.client ? window?.location?.href : undefined
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page {
  &__name {
    margin-left: auto;
    margin-right: auto;
    display: inline-block;
  }
  &__title {
    max-width: 720px;
    margin-left: auto;
    margin-right: auto;
  }
}
.article {
  &__author {
    font-size: 16px;
    margin-top: 1em;
  }
  &__metas {
    font-size: 13px;
    margin-top: 1em;
    display: flex;
    gap: .5em;
    align-items: center;
    justify-content: center;
    > * {
      min-width: 0;
    }
  }
  &__actions {
    display: flex;
    align-items: center;
    gap: 1rem;
    > * {
      min-width: 0;
    }
  }
  &__breadcrumb {
    flex: 1;
    ::v-deep {
      span {
        font-size: 1rem;
      }
    }
  }
  &__share {
    display: flex;
    gap: .75rem;
    color: $--color-primary;
    i {
      font-size: 1.75rem;
    }
  }
  &__wrapper {
    max-width: 640px;
    margin: 3rem auto;
    font-size: 16px;
  }
  &__content {
    line-height: 175%;
    color: darken($--color-text-secondary, 15);
    ::v-deep {
      h1 {
        font-size: 1.5em;
        color: $--color-text-regular;
      }
      h2 {
        font-size: 1.35em;
        color: $--color-text-regular;
      }
      h3 {
        font-size: 1.25em;
        color: $--color-text-regular;
      }
      h4 {
        font-size: 1.2em;
        color: $--color-text-regular;
      }
      h5, h6 {
        font-size: 1em;
        color: $--color-text-regular;
      }
      img {
        max-width: 100%;
        height: auto;
      }
      strong, b {
        color: $--color-text-regular;
      }
      a {
        color: $--color-primary;
        text-decoration: underline;
      }
      .embedded {
        &-asset {
          text-align: center;
          img {
            border-radius: $--radius;
          }
        }
        &-player {
          position: relative;
          width: 100%;
          padding: 0;
          margin: 1.5rem 0;
          height: 0;
          padding-bottom: calc(100% / 16 * 9);
          iframe {
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            border-radius: $--radius;
          }
        }
      }
    }
  }
}
</style>
